import { computed, type Ref } from "vue"
import type { GeneralInformation, MasterDropdown } from "../../entities/NSwagGenerated"

export interface CompareOption {
	id: string
	generalInformationId: string
	quantity: number
	formatId: string
}

interface UseCompareOptionsPersistenceArgs {
	assessmentId: Ref<string>
	tabId: Ref<string>
	compareOptions: Ref<CompareOption[]>
	selectedCompareOptionIds: Ref<string[]>
	generalInformationOptions: Ref<GeneralInformation[]>
	resultFormatOptionsWithLabels: Ref<(MasterDropdown & { displayLabel: string })[]>
	restoreState: Ref<boolean>
	canRestore: Ref<boolean>
}

export const useCompareOptionsPersistence = ({
	assessmentId,
	tabId,
	compareOptions,
	selectedCompareOptionIds,
	generalInformationOptions,
	resultFormatOptionsWithLabels,
	restoreState,
	canRestore,
}: UseCompareOptionsPersistenceArgs) => {
	const compareSelectionStorageKey = computed(() => `compareSelection:${assessmentId.value}:${tabId.value}`)

	const saveCompareOptions = () => {
		if (compareOptions.value.length === 0) {
			localStorage.removeItem(compareSelectionStorageKey.value)
			return
		}

		localStorage.setItem(compareSelectionStorageKey.value, JSON.stringify(compareOptions.value))
	}

	const restoreCompareOptions = () => {
		if (!canRestore.value || restoreState.value || !generalInformationOptions.value.length || !resultFormatOptionsWithLabels.value.length) {
			return
		}

		const availableGeneralInformationIds = new Set(generalInformationOptions.value.map((option) => option.id).filter(Boolean))
		const availableFormatIds = new Set(resultFormatOptionsWithLabels.value.map((option) => option.id).filter(Boolean))

		let storedOptions: CompareOption[] = []

		try {
			const storedValue = localStorage.getItem(compareSelectionStorageKey.value)
			const parsedValue = storedValue ? JSON.parse(storedValue) : []
			storedOptions = Array.isArray(parsedValue) ? parsedValue : []
		} catch {
			storedOptions = []
		}

		compareOptions.value = storedOptions.filter((option) => {
			return availableGeneralInformationIds.has(option.generalInformationId) && availableFormatIds.has(option.formatId)
		})
		selectedCompareOptionIds.value = compareOptions.value.map((option) => option.id)
		restoreState.value = true

		if (compareOptions.value.length !== storedOptions.length) {
			saveCompareOptions()
		}
	}

	return {
		saveCompareOptions,
		restoreCompareOptions,
	}
}
